/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_META } from '@/lib/utils-cat';
import { LogOut, ShieldCheck, Sun, Moon, Monitor, Palette, User as UserIcon, Smartphone, Info, MessageCircle, Lightbulb, Globe, BadgeCheck } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { AVAILABLE_LANGUAGES } from '@/i18n';
import AccessibilityCard from '@/components/AccessibilityCard';
import { toast } from 'sonner';
import { SectionHeader } from '@/components/settings/SectionHeader';
import { InstallAppCard } from '@/components/settings/InstallAppCard';
import { PushNotificationsCard } from '@/components/settings/PushNotificationsCard';

const APP_VERSION = '1.0.0';

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { logout, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const langs = AVAILABLE_LANGUAGES;
  const change = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('sc_lang', code);
    toast.success(langs.find(x => x.code === code)?.label || code);
  };
  const catMeta = user ? CATEGORY_META[user.category] : null;
  const CatIcon = catMeta?.icon;

  const themes = [
    { key: 'light', label: t('light'), Icon: Sun },
    { key: 'dark', label: t('dark'), Icon: Moon },
    { key: 'system', label: t('system'), Icon: Monitor },
  ];

  return (
    <div className="space-y-5 max-w-3xl mx-auto pb-8" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">{t('settings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('appName')} · v{APP_VERSION}</p>
      </div>

      {/* Aspetto */}
      <Card className="p-5 rounded-2xl bg-card" data-testid="settings-appearance-card">
        <SectionHeader Icon={Palette} title={t('settingsAppearance')} subtitle={t('settingsAppearanceDesc')} color="#7C3AED" />

        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('theme')}</div>
            <div className="grid grid-cols-3 gap-2" data-testid="settings-theme-grid">
              {themes.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setTheme(key)}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-colors ${theme === key ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary/60'}`}
                  data-testid={`settings-theme-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  <div className="text-xs font-semibold">{label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Globe className="h-3 w-3" /> {t('language')} · {langs.length}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="settings-lang-grid">
              {langs.map(l => (
                <button
                  key={l.code}
                  onClick={() => change(l.code)}
                  className={`p-2.5 rounded-xl border text-left flex items-center gap-2 transition-colors ${i18n.language === l.code ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary/60'}`}
                  data-testid={`settings-lang-${l.code}`}
                >
                  <span className="text-xl">{l.flag}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{l.label}</div>
                    <div className="text-[10px] text-muted-foreground uppercase">{l.code}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Account */}
      <Card className="p-5 rounded-2xl bg-card" data-testid="settings-account-card">
        <SectionHeader Icon={UserIcon} title={t('settingsAccount')} subtitle={t('settingsAccountDesc')} color="var(--wl-primary)" />

        <div className="rounded-xl border border-border p-4 bg-secondary/30 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground text-xs">{t('fullName')}:</span>
            <span className="font-semibold truncate">{user?.full_name}</span>
            {user?.is_admin && <BadgeCheck className="h-4 w-4 text-primary flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground text-xs w-14">{t('email')}:</span>
            <span className="truncate">{user?.email}</span>
          </div>
          {catMeta && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground text-xs w-14">{t('category')}:</span>
              <span
                className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5"
                style={{ backgroundColor: catMeta.soft, color: catMeta.textOnSoft }}
              >
                {CatIcon && <CatIcon className="h-3 w-3" />}
                {t(catMeta.label)}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={() => navigate('/profile')} data-testid="settings-goto-profile">
            {t('editProfile')}
          </Button>
          {user?.is_admin && (
            <Button variant="secondary" onClick={() => navigate('/admin')} data-testid="settings-goto-admin">
              <ShieldCheck className="h-4 w-4 mr-1" /> {t('adminPanel')}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => { logout(); navigate('/login'); }}
            className="col-span-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="settings-logout-button"
          >
            <LogOut className="h-4 w-4 mr-1" /> {t('logout')}
          </Button>
        </div>
      </Card>

      {/* App / PWA */}
      <Card className="p-5 rounded-2xl bg-card" data-testid="settings-app-card">
        <SectionHeader Icon={Smartphone} title={t('settingsApp')} subtitle={t('settingsAppDesc')} color="#1D4ED8" />
        <InstallAppCard />
        <PushNotificationsCard />
      </Card>

      {/* Accessibilità - Sprint E */}
      <AccessibilityCard />

      {/* Supporto e info */}
      <Card className="p-5 rounded-2xl bg-card" data-testid="settings-support-card">
        <SectionHeader Icon={Info} title={t('settingsSupport')} subtitle={t('settingsSupportDesc')} color="#B45309" />

        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('quickActions')}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <button
            onClick={() => navigate('/feedback')}
            className="p-3 rounded-xl border border-border hover:bg-secondary/60 text-left flex items-center gap-3 transition-colors"
            data-testid="settings-feedback-btn"
          >
            <Lightbulb className="h-4 w-4 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">{t('sendFeedback')}</div>
              <div className="text-xs text-muted-foreground truncate">{t('feedbackSubtitle')}</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/wellness')}
            className="p-3 rounded-xl border border-border hover:bg-secondary/60 text-left flex items-center gap-3 transition-colors"
            data-testid="settings-wellness-btn"
          >
            <MessageCircle className="h-4 w-4 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">{t('wellness')}</div>
              <div className="text-xs text-muted-foreground truncate">{t('wellnessSubtitle')}</div>
            </div>
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('aboutApp')}</div>
          <p className="text-sm text-muted-foreground mb-3">{t('aboutAppDesc')}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/60">
              {t('versionLabel')}: <b className="text-foreground">{APP_VERSION}</b>
            </span>
            <button
              type="button"
              onClick={() => navigate('/legal/privacy')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              data-testid="settings-privacy-link"
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => navigate('/legal/cookie')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              data-testid="settings-cookie-link"
            >
              Cookie Policy
            </button>
            <button
              type="button"
              onClick={() => navigate('/legal/dpa')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              data-testid="settings-dpa-link"
            >
              DPA
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SettingsPage;
